import { z } from 'zod';
import { SchemaHookKind, parseWithHookCfg, schemaWithHook } from '../schemaWithHook';
import type { AppletDesignConfig } from '@redviking/argonaut-util/types/mes/applet-designs/applet-design-config.zod';
import type { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import { deployParamValueSchema as latestDeployParamValueSchema } from '@redviking/argonaut-util/types/mes/applet-designs/latest/deployParam.latest.zod';
import { upgradeDeployParamFromV3ToV4 } from './migration-functions/deployParam.v3tov4';
import type { DeployParamValue_v3 } from '@redviking/argonaut-util/types/mes/applet-designs/v3/deployParam.v3.zod';
import { DeployParamKind } from './deployParamKind';
import { latestAppletDesignVersionNumber } from '@redviking/argonaut-util/types/mes/applet-designs/latest/schemaVersion';

export { DeployParamKind };

const deployParamValueSchema = z.preprocess((val, ctx) => {
    try {
        if (typeof val !== 'object') {
            throw new Error(`Incoming deployment param value is of type: ${typeof val}.`);
        }
        if (val === null) {
            throw new Error('Incoming deployment param value is null.');
        }
        const schemaVersion: number = 'schemaVersion' in val && typeof val.schemaVersion === 'number'
            ? val.schemaVersion
            : 3; // assume v3 since it was introduced in v4
        if (typeof schemaVersion !== 'number' || schemaVersion < 0 || schemaVersion > latestAppletDesignVersionNumber) {
            throw new Error(`Incoming deployment param value has invalid schema version: ${schemaVersion}.`);
        }
        if (schemaVersion === 3) {
            return upgradeDeployParamFromV3ToV4(val as DeployParamValue_v3);
        } else if (schemaVersion === latestAppletDesignVersionNumber) {
            return val as Latest.AppletDesignVersionConfig;
        } else {
            throw new Error(`Incoming Applet Design with schema version ${schemaVersion} cannot me migrated.`);
        }
    } catch (e) {
        ctx.addIssue({
            fatal: true,
            code: 'custom',
            message: e instanceof Error ? e.message : `unknown error - ${JSON.stringify(e)}`,
        });
    }
    return val;
}, latestDeployParamValueSchema);

/** keyed by deployParamId */
export const deployParamValuesSchema = z.record(deployParamValueSchema);

export type DeployParamValue = z.infer<typeof deployParamValuesSchema>[string];

/**
 * creates a schema that can be captured by `getDeployParams`
 */
export function deployParamSchema<SCHEMA extends z.ZodType<{ deployParamKind: DeployParamKind }>> (mySchema: SCHEMA): SCHEMA {
    return schemaWithHook({
        schema: mySchema,
        hook: SchemaHookKind.DeployParam,
    });
}

export type DeployParam = Latest.AppletVariables.DeploymentParamAppletVar | Latest.LocationDeployParam | Latest.SparkplugAppletNodeCfgDeployParam | Latest.AssetDeployParam;

/**
 * this function exists because some deploy param kinds have a deployParamId that is derived
 */
export function getDeployParamId (paramCfg: DeployParam): string {
    if (paramCfg.deployParamKind === DeployParamKind.localVarValue) {
        return `${paramCfg.name} - Initial Value`;
    }
    if (paramCfg.deployParamKind === DeployParamKind.location) {
        return paramCfg.deployParamId;
    }
    if (paramCfg.deployParamKind === DeployParamKind.asset) {
        return paramCfg.deployParamId;
    }
    if (paramCfg.deployParamKind === DeployParamKind.sparkplugNodeCfg) {
        return paramCfg.deployParamId;
    }
    // @ts-expect-error - ensures cases are implemented
    const unknownDeployParamKind: string = paramCfg.deployParamKind;
    throw new Error(`unknown deploy param kind: ${unknownDeployParamKind}`);
}

export type DeployParamWithId = DeployParam & { deployParamId: string };

export function getDeployParams (cfg: AppletDesignConfig, schema: z.ZodType): DeployParamWithId[] {
    const deployParams: DeployParamWithId[] = [];
    parseWithHookCfg({
        schema,
        hookCfg: {
            [SchemaHookKind.DeployParam]: deployParam => {
                // only add the deploy param if it's not already in the list
                const deployParamId = getDeployParamId(deployParam);
                if (!deployParams.some(dp => dp.deployParamId === deployParamId && dp.deployParamKind === deployParam.deployParamKind)) {
                    deployParams.push({
                        ...deployParam,
                        deployParamId: getDeployParamId(deployParam),
                    });
                }
                return deployParam;
            },
        },
    }, cfg);
    return deployParams;
}
